import { Badge, Button, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Sparkline } from '@repletoryreach/sdk-ui/charts';
import { formatInteger, formatRate, formatRelative, humanBytes, humanDuration } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { OpsAuditPanel } from './OpsAuditPanel';
import { BACKLOG_WARN, hasNoConsumers, isDeadLetterQueue, queueRate, queueTone, type Queue, type QueueSamples } from './queueState';

const MUTED = 'var(--rr-color-text-muted)';

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap" gap="md">
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall" fw={600} c={tone}>{value}</Text>
    </Group>
  );
}

/**
 * One queue, opened from the table.
 *
 * Since 5 September the ops endpoint also carries the broker's publish, deliver
 * and acknowledge rates, the queue's memory, its message TTL and how long it
 * has been idle, so the depth is no longer the only thing this can say. A rate
 * of 0 on a queue that has never carried a message is the broker's own answer:
 * it omits `message_stats` until there is something to report.
 *
 * Since 7 September the write path exists, so the drawer also offers the
 * requeue action and the audit trail for this one queue. The trail sits beside
 * the button on purpose: what was already moved, by whom and why is the context
 * an operator needs before moving more.
 */
export function QueueDetailDrawer({ queue, history, onClose, onRequeue }: {
  queue: Queue | null;
  history: QueueSamples | undefined;
  onClose: () => void;
  onRequeue: (q: Queue) => void;
}) {
  const t = useT();
  const rate = queue ? queueRate(queue, history) : null;

  return (
    <Drawer opened={queue !== null} onClose={onClose} title={t('queues.detail.title')} size="md">
      {queue && (
        <Stack gap="lg">
          <Stack gap="xs">
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={5}><code>{queue.name}</code></Title>
              <StatusBadge status={queueTone(queue)} size="xs">{queue.state ?? t('status.unknown')}</StatusBadge>
              {hasNoConsumers(queue) && (
                <Badge size="xs" color="red" variant="light">{t('queues.noConsumersBadge')}</Badge>
              )}
              {isDeadLetterQueue(queue) && (
                <Badge size="xs" color="orange" variant="light">{t('queues.deadLetterBadge')}</Badge>
              )}
            </Group>
          </Stack>

          {/* On a dead-letter queue the action is the point of the drawer, so
              it leads with what the messages are rather than sitting under the
              throughput figures. */}
          {isDeadLetterQueue(queue) ? (
            <Alert color="warning" title={t('queues.detail.deadLetterTitle')}>
              <Stack gap="sm">
                <Text role="bodySmall">{t('queues.detail.deadLetterBody')}</Text>
                <Group>
                  <Button size="sm" color="orange" onClick={() => onRequeue(queue)} disabled={(queue.messages ?? 0) === 0}>
                    {t('queues.requeue.action')}
                  </Button>
                </Group>
                {(queue.messages ?? 0) === 0 && (
                  <Text role="caption" c={MUTED}>{t('queues.detail.deadLetterEmpty')}</Text>
                )}
              </Stack>
            </Alert>
          ) : (
            <Group>
              <Button size="sm" variant="outline" onClick={() => onRequeue(queue)}>
                {t('queues.requeue.action')}
              </Button>
            </Group>
          )}

          <Stack gap="sm">
            <Row label={t('queues.columns.consumers')} value={formatInteger(queue.consumers ?? 0)} />
            <Row
              label={t('queues.columns.ready')}
              value={formatInteger(queue.messages_ready ?? 0)}
              tone={(queue.messages_ready ?? 0) > BACKLOG_WARN ? 'var(--rr-color-warning)' : undefined}
            />
            <Row
              label={t('queues.columns.unacked')}
              value={formatInteger(queue.messages_unacked ?? 0)}
              tone={(queue.messages_unacked ?? 0) > 0 ? 'var(--rr-color-error)' : undefined}
            />
            <Row label={t('common.total')} value={formatInteger(queue.messages ?? 0)} />
            <Row label={t('queues.columns.vhost')} value={queue.vhost} />
          </Stack>

          <Stack gap="sm">
            <Text role="bodySmall" fw={600}>{t('queues.detail.throughput')}</Text>
            <Row label={t('queues.detail.publishRate')} value={formatRate(rate?.publish ?? 0)} />
            <Row label={t('queues.detail.deliverRate')} value={formatRate(rate?.deliver ?? 0)} />
            <Row label={t('queues.detail.published')} value={formatInteger(queue.published_total ?? 0)} />
            <Row label={t('queues.detail.acked')} value={formatInteger(queue.acked_total ?? 0)} />
            <Row label={t('queues.detail.memory')} value={humanBytes(queue.memory_bytes ?? 0)} />
            {queue.message_ttl_ms != null && (
              <Row label={t('queues.detail.ttl')} value={humanDuration(queue.message_ttl_ms)} />
            )}
            {queue.idle_since && (
              <Row label={t('queues.detail.idleSince')} value={formatRelative(queue.idle_since)} />
            )}
            <Text role="caption" c={MUTED}>
              {rate?.measured
                ? t('queues.detail.throughputHint', { polls: history?.at.length ?? 0 })
                : t('queues.detail.throughputHintBroker')}
            </Text>
          </Stack>

          {history && history.ready.length > 1 && (
            <Stack gap="xs">
              <Text role="bodySmall" fw={600}>{t('queues.detail.readyTrend')}</Text>
              <Sparkline data={history.ready} h={70} w="100%" color="var(--rr-color-warning)" />
              <Text role="caption" c={MUTED}>{t('queues.detail.readyTrendHint', { count: history.ready.length })}</Text>
            </Stack>
          )}

          {hasNoConsumers(queue) && (
            <Text role="bodySmall" c="var(--rr-color-error)">{t('queues.detail.starvedHint')}</Text>
          )}

          {/* Scoped to this queue: the platform-wide trail is on the page. */}
          <OpsAuditPanel source={queue.name} limit={5} compact />

          <Text role="caption" c={MUTED}>{t('queues.detail.limits')}</Text>
        </Stack>
      )}
    </Drawer>
  );
}
